/*
 * ReferFriendCard — used in the customer profile /profile page (Iter 76)
 * ======================================================================
 * Small card + modal form to submit a referral. Referrer info auto-filled
 * from the authenticated customer. POSTs to /api/referrals (public).
 */
import { useState } from "react";
import axios from "axios";
import { Handshake, X, CheckCircle2, Gift } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ReferFriendCard = ({ profile }) => {
  const [showModal, setShowModal] = useState(false);
  return (
    <>
      <div
        className="bg-gradient-to-br from-[#F5A623]/15 via-[#0a0a0a] to-[#0a0a0a] border border-[#F5A623]/25 rounded-lg p-5"
        data-testid="refer-friend-card"
      >
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-full bg-[#F5A623]/15 border border-[#F5A623]/40 flex items-center justify-center shrink-0">
            <Gift size={20} className="text-[#F5A623]" />
          </div>
          <div className="flex-1">
            <h3 className="text-white font-semibold text-sm mb-1">
              Σύστησε έναν φίλο
            </h3>
            <p className="text-zinc-400 text-xs leading-relaxed mb-3">
              Ξέρεις κάποιον που θα ταίριαζε στη Λευτεριά FC Academy;
              Στείλε μας το όνομά του και θα φροντίσουμε τα υπόλοιπα — και
              θα ανταμειφθείς όταν εγγραφεί.
            </p>
            <button
              onClick={() => setShowModal(true)}
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#F5A623] text-black text-sm font-semibold rounded hover:bg-[#e6951a]"
              data-testid="refer-open-btn"
            >
              <Handshake size={14} /> Στείλε παραπομπή
            </button>
          </div>
        </div>
      </div>

      {showModal && (
        <ReferModal profile={profile} onClose={() => setShowModal(false)} />
      )}
    </>
  );
};

const ReferModal = ({ profile, onClose }) => {
  const [f, setF] = useState({
    referred_name: "",
    referred_phone: "",
    referred_email: "",
    referred_age: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.referred_name.trim()) return setError("Απαιτείται το όνομα.");
    setError("");
    setSubmitting(true);
    try {
      await axios.post(`${API}/referrals`, {
        referrer_name: profile?.name || "",
        referrer_email: profile?.email || null,
        referrer_phone: profile?.phone || null,
        referred_name: f.referred_name.trim(),
        referred_phone: f.referred_phone.trim() || null,
        referred_email: f.referred_email.trim() || null,
        referred_age: f.referred_age ? Number(f.referred_age) : null,
        source: "website",
        notes: f.notes.trim() || null,
      });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Σφάλμα υποβολής.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 overflow-y-auto"
      data-testid="refer-modal"
    >
      <div className="bg-[#0a0a0a] border border-[#262626] rounded-xl w-full max-w-md">
        <div className="flex items-start justify-between p-5 border-b border-[#1e1e1e]">
          <div>
            <p className="text-[#F5A623] text-[10px] uppercase tracking-widest mb-1">
              Πρόγραμμα Συστάσεων
            </p>
            <h3 className="font-['Bebas_Neue'] text-xl text-white tracking-wide">
              Σύστησε έναν φίλο
            </h3>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-white">
            <X size={20} />
          </button>
        </div>

        {done ? (
          <div className="p-8 text-center">
            <CheckCircle2 size={48} className="mx-auto text-emerald-400 mb-3" />
            <h4 className="text-white font-semibold text-lg mb-1">
              Ευχαριστούμε!
            </h4>
            <p className="text-zinc-400 text-sm">
              Θα επικοινωνήσουμε σύντομα με τον/την <strong>{f.referred_name}</strong>.
            </p>
            <button
              onClick={onClose}
              className="mt-5 px-6 py-2 bg-[#F5A623] text-black text-sm font-semibold rounded hover:bg-[#e6951a]"
            >
              Κλείσιμο
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-5 space-y-3">
            <div className="p-3 rounded bg-black/40 border border-[#1e1e1e]">
              <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
                Από
              </p>
              <p className="text-white text-sm">{profile?.name || "—"}</p>
              <p className="text-zinc-500 text-xs">
                {profile?.email || profile?.phone || ""}
              </p>
            </div>

            <input
              value={f.referred_name}
              onChange={(e) => set("referred_name", e.target.value)}
              placeholder="Ονοματεπώνυμο φίλου *"
              className="w-full bg-black border border-[#262626] rounded px-3 py-2 text-sm text-white"
              data-testid="refer-name"
              required
            />
            <div className="grid grid-cols-2 gap-2">
              <input
                type="tel"
                value={f.referred_phone}
                onChange={(e) => set("referred_phone", e.target.value)}
                placeholder="Τηλέφωνο"
                className="bg-black border border-[#262626] rounded px-3 py-2 text-sm text-white"
                data-testid="refer-phone"
              />
              <input
                type="number"
                value={f.referred_age}
                onChange={(e) => set("referred_age", e.target.value)}
                placeholder="Ηλικία"
                min={3}
                max={99}
                className="bg-black border border-[#262626] rounded px-3 py-2 text-sm text-white"
              />
            </div>
            <input
              type="email"
              value={f.referred_email}
              onChange={(e) => set("referred_email", e.target.value)}
              placeholder="Email"
              className="w-full bg-black border border-[#262626] rounded px-3 py-2 text-sm text-white"
            />
            <textarea
              value={f.notes}
              onChange={(e) => set("notes", e.target.value)}
              placeholder="Σημειώσεις (προαιρετικό)"
              rows={2}
              className="w-full bg-black border border-[#262626] rounded px-3 py-2 text-sm text-white"
            />
            {error && (
              <p className="text-red-400 text-xs" data-testid="refer-error">
                {error}
              </p>
            )}
            <button
              type="submit"
              disabled={submitting}
              className="w-full py-2.5 bg-[#F5A623] text-black text-sm font-semibold rounded hover:bg-[#e6951a] disabled:opacity-40"
              data-testid="refer-submit"
            >
              {submitting ? "Αποστολή…" : "Υποβολή"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ReferFriendCard;
